import React, { useState, useEffect } from 'react';
import { Calculator, Leaf, Factory, Plane, Car, Trash2, Zap, Info, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../services/api';

const EMISSION_SOURCES = [
  { id: 'electricity', label: 'Electricity', icon: Zap, unit: 'kWh', scope: 2, desc: 'Annual electricity consumption' },
  { id: 'natural_gas', label: 'Natural Gas', icon: Factory, unit: 'kWh', scope: 1, desc: 'Heating and cooking' },
  { id: 'heating_oil', label: 'Heating Oil', icon: Factory, unit: 'liters', scope: 1, desc: 'Oil-based heating' },
  { id: 'diesel', label: 'Diesel (Vehicles)', icon: Car, unit: 'liters', scope: 1, desc: 'Company vehicles' },
  { id: 'petrol', label: 'Petrol (Vehicles)', icon: Car, unit: 'liters', scope: 1, desc: 'Company vehicles' },
  { id: 'business_flights', label: 'Business Flights', icon: Plane, unit: 'km', scope: 3, desc: 'Total flight distance' },
  { id: 'business_car', label: 'Business Car Travel', icon: Car, unit: 'km', scope: 3, desc: 'Business trips by car' },
  { id: 'employee_commuting', label: 'Employee Commuting', icon: Car, unit: 'km/year', scope: 3, desc: 'Total annual commute' },
  { id: 'waste', label: 'Waste Generated', icon: Trash2, unit: 'tonnes', scope: 3, desc: 'Total waste to landfill' },
];

const COUNTRIES = [
  { code: 'north_macedonia', name: 'North Macedonia', ef: 0.58 },
  { code: 'eu_average', name: 'EU Average', ef: 0.29 },
  { code: 'germany', name: 'Germany', ef: 0.35 },
  { code: 'france', name: 'France', ef: 0.05 },
  { code: 'uk', name: 'United Kingdom', ef: 0.21 },
  { code: 'usa', name: 'United States', ef: 0.42 },
  { code: 'default', name: 'Other (Global Avg)', ef: 0.40 },
];

export default function CarbonCalculator({ executionId, onCalculated, initialData }) {
  const [inputs, setInputs] = useState({
    electricity_kwh: 0,
    country: 'default',
    natural_gas_kwh: 0,
    heating_oil_liters: 0,
    diesel_liters: 0,
    petrol_liters: 0,
    business_flight_km: 0,
    business_car_km: 0,
    employee_commuting_km: 0,
    waste_tonnes: 0,
    revenue_eur: 0,
  });
  
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    if (initialData?.input) {
      setInputs(prev => ({ ...prev, ...initialData.input }));
      if (initialData.result) {
        setResult(initialData.result);
      }
    }
  }, [initialData]);

  const handleInputChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    setInputs(prev => ({ ...prev, [field]: numValue }));
  };

  const calculate = async () => {
    setLoading(true);
    try {
      const response = await api.calculateCarbon(inputs);
      setResult(response);
      if (onCalculated) onCalculated(response);
    } catch (err) {
      console.error('Calculation error:', err);
      alert('Failed to calculate: ' + err.message);
    }
    setLoading(false);
  };

  const saveToExecution = async () => {
    if (!executionId) return;
    setLoading(true);
    try {
      await api.saveCarbonForExecution(executionId, inputs);
      alert('Carbon data saved successfully!');
    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to save: ' + err.message);
    }
    setLoading(false);
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 40) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreLabel = (score) => {
    if (score >= 70) return 'Good';
    if (score >= 40) return 'Moderate';
    return 'High Impact';
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="carbon-calculator">
      {/* Header */}
      <div 
        className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-lg">
            <Calculator className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold">SME Carbon Calculator</h3>
            <p className="text-emerald-100 text-xs">Calculate Scope 1, 2, 3 emissions</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {result && (
            <span className="bg-white/20 text-white px-3 py-1 rounded-lg text-sm font-medium">
              {result.total} tCO2e
            </span>
          )}
          {expanded ? <ChevronUp className="w-5 h-5 text-white" /> : <ChevronDown className="w-5 h-5 text-white" />}
        </div>
      </div>

      {expanded && (
        <div className="p-5 space-y-6">
          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-blue-800 font-medium">How it works</p>
                <p className="text-xs text-blue-700 mt-1">
                  Enter your company's operational data below. The calculator uses standard emission factors 
                  (UK DEFRA, IEA, EPA) to compute your carbon footprint across all three scopes.
                </p>
              </div>
            </div>
          </div>

          {/* Country Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Your Country (for electricity factor)</label>
            <select
              value={inputs.country}
              onChange={(e) => setInputs(prev => ({ ...prev, country: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-emerald-500 outline-none"
            >
              {COUNTRIES.map(c => (
                <option key={c.code} value={c.code}>
                  {c.name} ({c.ef} kg CO2e/kWh)
                </option>
              ))}
            </select>
          </div>

          {/* Input Sections by Scope */}
          {[1, 2, 3].map(scope => (
            <div key={scope} className="space-y-3">
              <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-xs text-white ${
                  scope === 1 ? 'bg-orange-500' : scope === 2 ? 'bg-blue-500' : 'bg-purple-500'
                }`}>
                  Scope {scope}
                </span>
                {scope === 1 && 'Direct Emissions'}
                {scope === 2 && 'Purchased Energy'}
                {scope === 3 && 'Value Chain'}
              </h4>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {EMISSION_SOURCES.filter(s => s.scope === scope).map(source => {
                  const fieldMap = {
                    electricity: 'electricity_kwh',
                    natural_gas: 'natural_gas_kwh',
                    heating_oil: 'heating_oil_liters',
                    diesel: 'diesel_liters',
                    petrol: 'petrol_liters',
                    business_flights: 'business_flight_km',
                    business_car: 'business_car_km',
                    employee_commuting: 'employee_commuting_km',
                    waste: 'waste_tonnes',
                  };
                  const field = fieldMap[source.id];
                  const Icon = source.icon;
                  
                  return (
                    <div key={source.id} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className="w-4 h-4 text-gray-500" />
                        <span className="text-xs font-medium text-gray-700">{source.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={inputs[field] || ''}
                          onChange={(e) => handleInputChange(field, e.target.value)}
                          placeholder="0"
                          className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-emerald-500 outline-none"
                        />
                        <span className="text-xs text-gray-500 whitespace-nowrap">{source.unit}</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{source.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Revenue for Intensity */}
          <div className="bg-gray-50 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Annual Revenue (optional, for intensity calculation)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={inputs.revenue_eur || ''}
                onChange={(e) => handleInputChange('revenue_eur', e.target.value)}
                placeholder="Enter revenue"
                className="flex-1 bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-emerald-500 outline-none"
              />
              <span className="text-gray-500">EUR</span>
            </div>
          </div>

          {/* Calculate Button */}
          <div className="flex gap-3">
            <button
              onClick={calculate}
              disabled={loading}
              data-testid="calculate-carbon-btn"
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              <Calculator className="w-5 h-5" />
              {loading ? 'Calculating...' : 'Calculate Emissions'}
            </button>
            {executionId && result && (
              <button
                onClick={saveToExecution}
                disabled={loading}
                className="px-6 py-3 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-xl transition-colors"
              >
                Save
              </button>
            )}
          </div>

          {/* Results */}
          {result && (
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h4 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Leaf className="w-5 h-5 text-emerald-600" />
                Your Carbon Footprint
              </h4>

              {/* Total */}
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-5 text-center">
                <p className="text-sm text-emerald-700 font-medium mb-1">Total Emissions</p>
                <p className="text-4xl font-bold text-emerald-800">{result.total} <span className="text-lg">tCO2e</span></p>
                {result.intensity_score && (
                  <div className="mt-3 flex items-center justify-center gap-2">
                    <span className={`text-sm font-medium ${getScoreColor(result.intensity_score)}`}>
                      Carbon Intensity: {getScoreLabel(result.intensity_score)}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({result.intensity_per_revenue} tCO2e/€M revenue)
                    </span>
                  </div>
                )}
              </div>

              {/* Scope Breakdown */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Scope 1', value: result.scope1, pct: result.scope1_percentage, color: 'orange' },
                  { label: 'Scope 2', value: result.scope2, pct: result.scope2_percentage, color: 'blue' },
                  { label: 'Scope 3', value: result.scope3, pct: result.scope3_percentage, color: 'purple' },
                ].map(s => (
                  <div key={s.label} className={`bg-${s.color}-50 border border-${s.color}-200 rounded-lg p-4 text-center`}
                    style={{ backgroundColor: s.color === 'orange' ? '#fff7ed' : s.color === 'blue' ? '#eff6ff' : '#faf5ff' }}>
                    <p className="text-xs font-medium text-gray-600">{s.label}</p>
                    <p className="text-xl font-bold text-gray-900">{s.value}</p>
                    <p className="text-xs text-gray-500">tCO2e ({s.pct}%)</p>
                  </div>
                ))}
              </div>

              {/* Top Sources */}
              {result.top_sources && result.top_sources.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">Top Emission Sources</p>
                  <div className="space-y-2">
                    {result.top_sources.map((s, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{s.source.replace('Scope ', 'S')}</span>
                        <span className="text-sm font-medium text-gray-900">{s.emissions} tCO2e</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
